import _ from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import useDebounce from "src/ui/hooks/useDebounce";
import { Field } from "src/ui/shared/form/TextField";

import {
    Box,
    ClickAwayListener,
    IconButton,
    InputAdornment,
    List,
    ListItemButton,
    ListSubheader,
    Paper,
    Popper,
    Typography,
    useTheme,
} from "@mui/material";
// Filter terms typed into the field are removable input tokens, one of the rounded shapes the UI conventions allow.
// eslint-disable-next-line no-restricted-imports
import Chip from "@mui/material/Chip";
import { LoadingIcon } from "@shared/Loading";
import { Tooltip } from "@shared/Tooltip";

import { useStyles } from "./QueryBuilderField.styles";
import QueryBuilder, { ActiveFilter, Draft, FilterableAttribute } from "./QueryBuilderField.utils";

// Enough to scroll through, few enough to render at once; typing narrows a longer list.
const maxSuggestions = 50;

const initialDraft: Draft = { step: "key", key: null, operator: QueryBuilder.getContainsHash(), suggestions: [] };

type PopupRow = { id: string; label: string } & (
    | { kind: "search" }
    | { kind: "attribute"; attribute: FilterableAttribute }
    | { kind: "filter"; operator: string; value: string }
);

type QueryBuilderFieldProps = {
    value?: string;
    onChange?: (value: string) => void;
    onClear?: () => void;
    onSubmit?: (value: string) => void;
    debounceWait?: number;
    loading?: boolean;
    name?: string;
    autoComplete?: string;
    placeholder?: string;
    options?: FilterableAttribute[];
};

export function QueryBuilderField({
    value = "",
    onChange,
    onClear,
    onSubmit,
    debounceWait = 700,
    loading = false,
    name,
    autoComplete,
    placeholder,
    options = [],
}: QueryBuilderFieldProps) {
    const isChipsMode = options.length > 0;
    const filters = isChipsMode ? QueryBuilder.parseStringToFilters(value) : [];
    const [draft, setDraft] = React.useState<Draft>(initialDraft);
    const [open, setOpen] = React.useState(false);
    const [activeIndex, setActiveIndex] = React.useState<number | null>(null);

    const inputValueFromProps = filters.length === 0 ? value : "";
    const inputSource = `${isChipsMode ? "chips" : "text"}:${value}`;
    const [typedInput, setTypedInput] = React.useState<{ source: string; text: string } | null>(null);
    const inputValue = typedInput?.source === inputSource ? typedInput.text : inputValueFromProps;
    const setInputValue = (text: string) => setTypedInput({ source: inputSource, text });

    const { t } = useTranslation(["pages", "common"]);
    const { classes, cx } = useStyles();
    const theme = useTheme();
    const listId = React.useId();
    const getOptionId = (index: number) => `${listId}-${index}`;

    const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);

    // useDebounce reaches the current `onChange` through a stable handle, so a caller passing a fresh function
    // identity on every render (as opposed to a stable useCallback/setState setter) doesn't reset the
    // pending debounce timer and drop the keystroke it was about to fire.
    const onChangeDebounced = useDebounce((newValue: string) => onChange?.(newValue), debounceWait);

    const trimmedInput = inputValue.trim();
    const draftLabel = draft.key ? QueryBuilder.getAttributeLabel(options, draft.key) : "";

    const handleFocus = () => {
        if (isChipsMode) {
            setOpen(true);
        }
    };

    const applySearch = () => {
        onChangeDebounced.cancel();
        onChange?.(trimmedInput);
        setOpen(false);
    };

    const selectAttribute = (attribute: FilterableAttribute) => {
        onChangeDebounced.cancel();
        // The typed text named a filter, so it must not stay applied as a free-text search.
        if (filters.length === 0 && value) {
            onChange?.("");
        }
        setDraft({
            step: "value",
            key: attribute.key,
            operator: QueryBuilder.getContainsHash(),
            suggestions: attribute.getSuggestions?.(filters) ?? [],
        });
        setInputValue("");
        setActiveIndex(null);
        setOpen(true);
        inputRef.current?.focus();
    };

    const addFilter = (operator: string, filterValue: string) => {
        if (!draft.key) {
            return;
        }
        const newFilter: ActiveFilter = { key: draft.key, operator, value: filterValue };
        onChange?.(QueryBuilder.parseFiltersToString([...filters, newFilter]));
        setDraft(initialDraft);
        setInputValue("");
        setActiveIndex(null);
        setOpen(false);
    };

    const toggleOperator = () => {
        setDraft((prev) => ({
            ...prev,
            operator:
                prev.operator === QueryBuilder.getEqualHash() ? QueryBuilder.getContainsHash() : QueryBuilder.getEqualHash(),
        }));
        inputRef.current?.focus();
    };

    const resetDraft = () => {
        setDraft(initialDraft);
        setInputValue("");
        setActiveIndex(null);
        setOpen(false);
    };

    const getActionRows = (): PopupRow[] => {
        if (!trimmedInput) {
            return [];
        }
        if (draft.step === "key") {
            // Free text and filter chips cannot be combined, so the search row is only offered before the first chip.
            return filters.length === 0
                ? [
                      {
                          id: "search",
                          label: t("pages:queryBuilderField.searchAllFields", { term: trimmedInput }),
                          kind: "search",
                      },
                  ]
                : [];
        }
        const labelKey =
            draft.operator === QueryBuilder.getEqualHash()
                ? "pages:queryBuilderField.applyValueEquals"
                : "pages:queryBuilderField.applyValueContains";
        return [
            {
                id: "value",
                label: t(labelKey, { field: draftLabel, value: trimmedInput }),
                kind: "filter",
                operator: draft.operator,
                value: trimmedInput,
            },
        ];
    };

    const getListRows = (): PopupRow[] => {
        const term = trimmedInput.toLowerCase();
        if (draft.step === "key") {
            return _.sortBy(options, "label")
                .filter((option) => option.label.toLowerCase().includes(term))
                .map((option) => ({ id: `key-${option.key}`, label: option.label, kind: "attribute", attribute: option }));
        }
        return draft.suggestions
            .filter((suggestion) => suggestion.toLowerCase().includes(term))
            .slice(0, maxSuggestions)
            .map((suggestion) => ({
                id: `value-${suggestion}`,
                label: suggestion,
                kind: "filter",
                operator: draft.operator,
                value: suggestion,
            }));
    };

    const actionRows = isChipsMode ? getActionRows() : [];
    const listRows = isChipsMode ? getListRows() : [];
    const allRows = [...actionRows, ...listRows];
    const isPopupOpen = open && allRows.length > 0;

    // Typing a field's name or a suggested value exactly makes Enter pick that entry instead of using the typed text.
    const exactMatchIndex = allRows.findIndex(
        (row, index) => index >= actionRows.length && row.label.toLowerCase() === trimmedInput.toLowerCase(),
    );
    // Nothing is highlighted until the user types or uses the arrow keys, so a stray Enter after focusing does nothing.
    const defaultIndex = trimmedInput ? Math.max(exactMatchIndex, 0) : -1;
    const currentIndex = Math.min(activeIndex ?? defaultIndex, allRows.length - 1);

    const selectRow = (row: PopupRow) => {
        if (row.kind === "search") {
            applySearch();
        } else if (row.kind === "attribute") {
            selectAttribute(row.attribute);
        } else {
            addFilter(row.operator, row.value);
        }
    };

    const moveActiveIndex = (step: number) => {
        const nextIndex =
            currentIndex < 0 ? (step > 0 ? 0 : allRows.length - 1) : (currentIndex + step + allRows.length) % allRows.length;
        setActiveIndex(nextIndex);
        setOpen(true);
        document.getElementById(getOptionId(nextIndex))?.scrollIntoView({ block: "nearest" });
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);

        if (!isChipsMode) {
            onChangeDebounced(newValue);
            return;
        }

        setActiveIndex(null);
        setOpen(true);
        if (draft.step === "key" && filters.length === 0) {
            // Until a key is picked, treat typing as free-text search; selectAttribute cancels this once chip mode is confirmed.
            if (!newValue.trim()) {
                onChangeDebounced.cancel();
                onChange?.("");
            } else {
                onChangeDebounced(newValue.trim());
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isChipsMode) {
            if (e.key === "Enter" && onSubmit) {
                e.preventDefault();
                onChangeDebounced.flush();
                onSubmit(inputValue);
            }
            return;
        }

        if ((e.key === "ArrowDown" || e.key === "ArrowUp") && allRows.length > 0) {
            e.preventDefault();
            moveActiveIndex(e.key === "ArrowDown" ? 1 : -1);
            return;
        }
        if (e.key === "Enter") {
            // A closed list is shown first, so Enter never acts on a row the user cannot see.
            if (!isPopupOpen) {
                setOpen(true);
            } else if (allRows[currentIndex]) {
                e.preventDefault();
                selectRow(allRows[currentIndex]);
            }
            return;
        }
        if (e.key === "Escape") {
            if (draft.step === "value") {
                resetDraft();
            } else {
                setOpen(false);
            }
        }
    };

    const removeFilter = (index: number) => {
        const newFilters = filters.filter((_f, i) => i !== index);
        onChange?.(QueryBuilder.parseFiltersToString(newFilters));
    };

    const handleClear = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.stopPropagation();
        e.preventDefault();
        onChangeDebounced.cancel();
        setDraft(initialDraft);
        setInputValue("");
        setActiveIndex(null);
        setOpen(false);
        if (isChipsMode) {
            onChange?.("");
        }
        onClear?.();
    };

    const getPlaceholder = () => {
        if (!isChipsMode) {
            return placeholder;
        }
        if (draft.step === "value") {
            return t("pages:queryBuilderField.placeholderValue");
        }
        return filters.length > 0 ? "" : t("pages:queryBuilderField.placeholderSearchOrFilter");
    };

    const renderRow = (row: PopupRow, index: number) => (
        <ListItemButton
            key={row.id}
            id={getOptionId(index)}
            role="option"
            aria-selected={index === currentIndex}
            selected={index === currentIndex}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => selectRow(row)}
        >
            {row.label}
        </ListItemButton>
    );

    const hasClearableContent = filters.length > 0 || inputValue.length > 0;

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Box ref={setAnchorEl} className={classes.root}>
                <Field
                    inputRef={inputRef}
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    onKeyDown={handleKeyDown}
                    placeholder={getPlaceholder()}
                    slotProps={{
                        htmlInput: {
                            style: { paddingLeft: theme.spacing(1) },
                            name,
                            autoComplete,
                            ...(isChipsMode && {
                                role: "combobox",
                                "aria-expanded": isPopupOpen,
                                "aria-controls": listId,
                                "aria-activedescendant": isPopupOpen && currentIndex >= 0 ? getOptionId(currentIndex) : undefined,
                            }),
                        },
                        input: {
                            startAdornment: (
                                <InputAdornment position="start" className={classes.startAdornment}>
                                    <i className={cx("fa-solid fa-magnifying-glass", classes.searchIcon)} />
                                    {isChipsMode ? (
                                        <Box className={classes.chipsArea}>
                                            {filters.map((filter, i) => {
                                                const attributeLabel = QueryBuilder.getAttributeLabel(options, filter.key);
                                                const label = `${attributeLabel} ${QueryBuilder.getOperatorLabel(filter.operator)} ${filter.value}`;
                                                return (
                                                    <Chip
                                                        key={label}
                                                        className={classes.chip}
                                                        size="small"
                                                        label={label}
                                                        onDelete={() => removeFilter(i)}
                                                    />
                                                );
                                            })}
                                            {draft.key ? (
                                                <>
                                                    <Chip
                                                        className={classes.draftChip}
                                                        size="small"
                                                        label={draftLabel}
                                                        onDelete={resetDraft}
                                                    />
                                                    <Tooltip title={t("pages:queryBuilderField.toggleOperator")}>
                                                        <Chip
                                                            className={classes.draftChip}
                                                            size="small"
                                                            label={QueryBuilder.getOperatorLabel(draft.operator)}
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onClick={toggleOperator}
                                                        />
                                                    </Tooltip>
                                                </>
                                            ) : null}
                                        </Box>
                                    ) : null}
                                </InputAdornment>
                            ),
                            endAdornment: loading ? (
                                <InputAdornment position="end">
                                    <LoadingIcon />
                                </InputAdornment>
                            ) : hasClearableContent ? (
                                <InputAdornment position="end">
                                    <Tooltip title={t("common:button.clear")}>
                                        <IconButton size="small" aria-label={t("common:button.clear")} onClick={handleClear}>
                                            <i className="fa-solid fa-xmark" />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ) : null,
                        },
                    }}
                />

                <Popper
                    open={isPopupOpen}
                    anchorEl={anchorEl}
                    placement="bottom-start"
                    style={{ zIndex: 1300, width: anchorEl?.clientWidth }}
                >
                    <Paper elevation={4}>
                        <List id={listId} role="listbox" dense className={classes.list}>
                            {actionRows.map(renderRow)}
                            {listRows.length > 0 ? (
                                <ListSubheader disableSticky role="presentation">
                                    {draft.step === "key"
                                        ? t("pages:queryBuilderField.filterBy")
                                        : t("pages:queryBuilderField.suggestions")}
                                </ListSubheader>
                            ) : null}
                            {listRows.map((row, i) => renderRow(row, actionRows.length + i))}
                        </List>
                        <Typography component="div" color="textSecondary" className={classes.hint}>
                            {draft.step === "key" ? t("pages:queryBuilderField.keyHint") : t("pages:queryBuilderField.valueHint")}
                        </Typography>
                    </Paper>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
}
